import { API_URL } from '../../utils/constants/api';
import axios from 'axios';
import Qs from 'qs';
import { unauthorized_status, forbidden_status } from '../../utils/constants/auth';
import history from '../../history';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '../../utils/tokenUtils';

export const addRequestObject = store => next => action => {
	const accessToken = getAccessToken();
	if (!accessToken) return next(action);
	if (!action.request || !action.request.url) {
		return next(action);
	}
	action.request.url = API_URL + action.request.url;
	if (!action.request.hasOwnProperty('headers')) {
		action.request.headers = {};
	}
	action.request.headers.Authorization = `Bearer ${accessToken.replace(/['"]+/g, '')}`;
	next(action);
};

export const instance = axios.create({
	paramsSerializer: params => Qs.stringify(params, { arrayFormat: 'repeat' }),
	baseURL: API_URL
});

const isTokenExpiringSoon = (token: string, bufferMinutes: number = 5): boolean => {
	try {
		const payload = JSON.parse(atob(token.split('.')[1]));
		const exp = payload.exp * 1000; 
		const now = Date.now();
		const buffer = bufferMinutes * 60 * 1000;
		return (exp - now) < buffer;
	} catch (e) {
		return true;
	}
};

const refreshToken = async () => {
	try {
		const refreshTokenValue = getRefreshToken();
		if (!refreshTokenValue) {
			throw new Error('No refresh token available');
		}

		const response = await axios.post(`${API_URL}/api/auth/token/refresh/`, {
			refresh: refreshTokenValue
		});

		const { access, refresh } = response.data;
		
		setTokens(access, refresh);
		setAxiosHeader(access);

		return access;
	} catch (error) {
		console.error('Token refresh failed:', error.response?.status, error.response?.data);
		if (error.response?.status === 401 || error.response?.status === 403) {
			clearTokens();
			localStorage.clear();
			history.push('/');
		}
		throw error;
	}
};

instance.interceptors.request.use(
	async config => {
		const accessToken = getAccessToken();
		if (accessToken) {
			if (isTokenExpiringSoon(accessToken)) {
				try {
					const newToken = await refreshToken();
					config.headers.Authorization = `Bearer ${newToken.replace(/['"]+/g, '')}`;
				} catch (error) {
					console.warn('Proactive token refresh failed, using existing token');
					config.headers.Authorization = `Bearer ${accessToken.replace(/['"]+/g, '')}`;
				}
			} else {
				config.headers.Authorization = `Bearer ${accessToken.replace(/['"]+/g, '')}`;
			}
		}
		return config;
	},
	error => Promise.reject(error)
);

instance.interceptors.response.use(
	response => response,
	async error => {
		const originalRequest = error.config;
		const { response: { status } } = error;

		if (status === unauthorized_status && !originalRequest._retry) {
			originalRequest._retry = true;
			
			try {
				const newToken = await refreshToken();
				originalRequest.headers.Authorization = `Bearer ${newToken.replace(/['"]+/g, '')}`;
				return instance(originalRequest);
			} catch (refreshError) {
				return Promise.reject(error);
			}
		}

		if (status === forbidden_status) {
			console.error('403 Forbidden - clearing localStorage:', error.config?.url);
			clearTokens();
			localStorage.clear();
			history.push('/');
		}

		return Promise.reject(error);
	}
);

export const setAxiosHeader = token => {
	if (token) {
		instance.defaults.headers.common.Authorization = `Bearer ${token}`;
	} else {
		instance.defaults.headers.common.Authorization = null;
	}
};
